/**
 * Layout catalogue.
 *
 * Thirteen families. Eleven of them take one of six directions; free-form and radial take none —
 * manual placement has no direction to have, and a radial map grows every way at once. Encoding that as a template-literal union means the sixty-one
 * valid combinations are checked by the compiler and the invalid ones — `free-form/down`, or a
 * family paired with a direction it does not support — cannot be written at all.
 */
export const UNDIRECTED_LAYOUT_FAMILIES = [
    'free-form',
    'radial',
    'metro',
    'timeline',
    'matrix',
    'board',
    'road',
    'layers',
];
/** The structures: whole-map diagrams with a geometry of their own. */
export const STRUCTURE_LAYOUTS = [
    'metro',
    'timeline',
    'matrix',
    'board',
    'road',
    'layers',
];
export function isStructureLayout(id) {
    return STRUCTURE_LAYOUTS.includes(id);
}
export const DIRECTIONAL_LAYOUT_FAMILIES = [
    'curved-line-tree',
    'direct-line-tree',
    'cornered-list',
    'cornered-tree',
    'simple-tree-curved',
    'simple-tree-direct',
    'simple-list-cornered',
    'simple-tree-cornered',
    'continuous-tree',
    'continuous-list',
    'fishbone',
];
export const LAYOUT_DIRECTIONS = [
    'right',
    'left',
    'left-right',
    'down',
    'up',
    'up-down',
];
export const LAYOUT_FAMILIES = {
    'free-form': { family: 'free-form', label: 'Free-form', connector: 'curved', inMvp: true },
    'curved-line-tree': {
        family: 'curved-line-tree',
        label: 'Curved line tree',
        connector: 'curved',
        inMvp: true,
    },
    'direct-line-tree': {
        family: 'direct-line-tree',
        label: 'Direct line tree',
        connector: 'direct',
        inMvp: true,
    },
    'simple-tree-direct': {
        family: 'simple-tree-direct',
        label: 'Simple tree direct',
        connector: 'direct',
        inMvp: true,
    },
    'cornered-list': {
        family: 'cornered-list',
        label: 'Cornered list',
        connector: 'cornered',
        inMvp: false,
    },
    'cornered-tree': {
        family: 'cornered-tree',
        label: 'Cornered tree',
        connector: 'cornered',
        inMvp: false,
    },
    'simple-tree-curved': {
        family: 'simple-tree-curved',
        label: 'Simple tree curved',
        connector: 'curved',
        inMvp: false,
    },
    'simple-list-cornered': {
        family: 'simple-list-cornered',
        label: 'Simple list cornered',
        connector: 'cornered',
        inMvp: false,
    },
    'simple-tree-cornered': {
        family: 'simple-tree-cornered',
        label: 'Simple tree cornered',
        connector: 'cornered',
        inMvp: false,
    },
    'continuous-tree': {
        family: 'continuous-tree',
        label: 'Continuous tree',
        connector: 'continuous',
        inMvp: false,
    },
    'continuous-list': {
        family: 'continuous-list',
        label: 'Continuous list',
        connector: 'continuous',
        inMvp: false,
    },
    radial: { family: 'radial', label: 'Radial', connector: 'curved', inMvp: false },
    fishbone: { family: 'fishbone', label: 'Fishbone', connector: 'direct', inMvp: false },
    metro: { family: 'metro', label: 'Metro map', connector: 'direct', inMvp: false },
    timeline: { family: 'timeline', label: 'Timeline', connector: 'direct', inMvp: false },
    matrix: { family: 'matrix', label: '2×2 matrix', connector: 'direct', inMvp: false },
    board: { family: 'board', label: 'Board', connector: 'direct', inMvp: false },
    road: { family: 'road', label: 'Road', connector: 'direct', inMvp: false },
    layers: { family: 'layers', label: 'Layers', connector: 'direct', inMvp: false },
};
/** The exact four layouts the MVP ships with. */
export const MVP_LAYOUTS = [
    'free-form',
    'curved-line-tree/left-right',
    'direct-line-tree/left-right',
    'simple-tree-direct/down',
];
/**
 * Every layout this build can arrange a map with: the MVP's four, and the three that followed.
 *
 * `cornered-tree/down` is the organisation chart — a downward tree drawn with square corners,
 * which is what an org chart is. It needed no new family, only registering.
 */
export const SHIPPED_LAYOUTS = [
    ...MVP_LAYOUTS,
    'radial',
    'cornered-tree/down',
    'fishbone/right',
    ...STRUCTURE_LAYOUTS,
];
/**
 * Layouts that arrange a whole map and cannot arrange one branch of it.
 *
 * A radial map is centred on its root and a fishbone is read from its head; neither means
 * anything hanging off the side of another layout, so a branch is only offered the trees.
 */
export const WHOLE_MAP_LAYOUTS = [
    'radial',
    'fishbone/right',
    ...STRUCTURE_LAYOUTS,
];
export function isWholeMapLayout(id) {
    return WHOLE_MAP_LAYOUTS.includes(id) || id.startsWith('fishbone/');
}
export const DEFAULT_LAYOUT = 'curved-line-tree/left-right';
export function layoutId(family, direction) {
    return `${family}/${direction}`;
}
/** Splits a layout id. `free-form` has no direction, and says so with `null`. */
export function parseLayoutId(id) {
    if (UNDIRECTED_LAYOUT_FAMILIES.includes(id)) {
        return { family: id, direction: null };
    }
    const separator = id.indexOf('/');
    return {
        family: id.slice(0, separator),
        direction: id.slice(separator + 1),
    };
}
export function isMvpLayout(id) {
    return MVP_LAYOUTS.includes(id);
}
/**
 * Does this layout place nodes on both sides of the root?
 *
 * The side-change operation only makes sense for these, and the editor hides the affordance
 * elsewhere rather than offering an action that silently does nothing.
 */
export function isTwoSided(id) {
    const { direction } = parseLayoutId(id);
    return direction === 'left-right' || direction === 'up-down';
}
//# sourceMappingURL=layout.js.map